import { useEffect, useState } from 'react';
import { getWsBaseUrl } from '../lib/apiClient';
import { getSessionToken } from '../lib/session';

type PresenceMessage = { type: 'presence'; count: number } | { type: 'pong' };

const BASE_BACKOFF_MS = 800;
const MAX_BACKOFF_MS = 15_000;
const MAX_ATTEMPTS = 6;
// Same rationale as useLotWs.ts: a 15s ping keeps the socket under Cloudflare's
// ~100s idle close and NAT keepalive, and feeds the zombie-socket check. The
// PresenceDO answers it via setWebSocketAutoResponse without waking from
// hibernation, so the heartbeat is free server-side.
const PING_INTERVAL_MS = 15_000;

function isPresenceMessage(value: unknown): value is PresenceMessage {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { type?: unknown; count?: unknown };
  if (v.type === 'pong') return true;
  return v.type === 'presence' && typeof v.count === 'number';
}

// One app-wide WebSocket to the singleton PresenceDO, live for the component's
// whole lifetime. Returns the current online-user count, or null until the first
// frame arrives (callers should render nothing rather than a fake number).
export function useOnlinePresence(): { count: number | null } {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    const token = getSessionToken();
    if (!token) return;

    let cancelled = false;
    let socket: WebSocket | null = null;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let pingTimer: number | null = null;
    let unbindVisibility: (() => void) | null = null;

    const connect = () => {
      if (cancelled) return;
      if (unbindVisibility) {
        unbindVisibility();
        unbindVisibility = null;
      }
      const url = `${getWsBaseUrl()}/api/presence/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      socket = ws;

      let pingsSinceLastPong = 0;

      const sendPing = () => {
        if (ws.readyState !== WebSocket.OPEN) return;
        if (pingsSinceLastPong >= 2) {
          console.warn('presence-ws.zombie_socket_detected');
          ws.close(4000, 'zombie');
          return;
        }
        pingsSinceLastPong += 1;
        ws.send(JSON.stringify({ type: 'ping' }));
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        sendPing();
      };

      ws.onopen = () => {
        if (cancelled) return;
        attempt = 0;
        pingsSinceLastPong = 0;
        if (pingTimer !== null) window.clearInterval(pingTimer);
        pingTimer = window.setInterval(sendPing, PING_INTERVAL_MS);
        if (typeof document !== 'undefined') {
          document.addEventListener('visibilitychange', handleVisibilityChange);
          unbindVisibility = () =>
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
      };

      ws.onmessage = (ev) => {
        if (cancelled) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        } catch {
          console.warn('presence-ws.message.parse_failed', {
            sample: String(ev.data).slice(0, 120),
          });
          return;
        }
        if (!isPresenceMessage(parsed)) return;
        if (parsed.type === 'pong') {
          pingsSinceLastPong = 0;
          return;
        }
        setCount(parsed.count);
      };

      ws.onerror = () => {
        if (cancelled) return;
        console.warn('presence-ws.error');
      };

      ws.onclose = (ev) => {
        if (cancelled) return;
        const clean = ev.code === 1000 || ev.code === 1005;
        if (clean) return;
        // After the backoff ceiling we stop retrying and the count freezes at its
        // last value (the chip is decorative — a stale number beats flicker). Leave
        // a breadcrumb so a presence socket that silently gave up is diagnosable,
        // rather than the connection dying with no trace.
        if (attempt >= MAX_ATTEMPTS) {
          console.warn('presence-ws.reconnect_exhausted', { attempts: attempt, lastCode: ev.code });
          return;
        }
        const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
        attempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (pingTimer !== null) window.clearInterval(pingTimer);
      if (unbindVisibility) unbindVisibility();
      if (socket && socket.readyState <= WebSocket.OPEN) {
        socket.close(1000, 'unmount');
      }
    };
  }, []);

  return { count };
}
